import { initialUiStateT } from "../types";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
  getProjectThunk,
  getCategoriesThunk,
  getCategoryInfoThunk,
  fetchTemplateFoldersThunk,
  createTemplateFolderThunk,
  fetchTemplatesThunk,
  getCurrentStageThunk,
  restoreTemplateThunk,
  getProjectStagesThunk,
  getProductsThunk,
  searchProductsThunk,
} from "./uiThunks";

import {
  IObject,
  IProjectConfigurationResponseData,
  ISelectItemObjectData,
  ITemplateFoldersResponseData,
  ICreateTemplateFolderResponseData,
  ITemplatesResponseData,
  ISearchData,
  IStudioCategory,
  IStudioStage,
  ISearchProjectsResponseData,
} from "./uiTypes";
import { IProductsResponseData } from "../../../processes/api/project";
import {
  QualityType,
  resolutionListT,
  resolutionList,
} from "../../../utils/constants";
import { ObjectService } from "../../../services/ObjectService";

// Default aspect ratio
const defaultAspect = resolutionList[0];
const { REACT_APP_NODE_ENV_KEY } = process.env;

const initialState: initialUiStateT = {
  activeTab: undefined,
  selectedModel: false,
  selectedType: "none",
  activeEnvironmentId: 0,
  activeObjectId: null,
  renderHash: "",
  camera: {
    fov: 52.5, //Field Of View з CameraSettings
    fieldOfView: 52.3,
    focalDistance: null, //Focal Distance з CameraSettings
    sensorWidth: null, //Sensor Width з CameraSettings
    exposure: null, //Exposure з CameraSettings
    // Enabled states for camera settings
    enabledSettings: {
      sensorWidth: false,
      focalDistance: false,
      exposure: false,
    },
    imageType: "png",
    transform: {
      //Формуєця на беці з up , forward, position
      scale: {
        x: 1,
        y: 1,
        z: 1,
      },
      position: {
        x: -645.57,
        y: 869.28,
        z: 31.83,
      },
      rotation: {
        yaw: -35,
        roll: 0,
        pitch: 2.6,
      },
    },
    imageWidth: 1920,
    renderType: "pathTracing",
    imageHeight: 1080,
    renderWidth: 3840,
    antiAliasing: "TSR",
    renderHeight: 2160,
    renderWarmup: 1024,
    rotationType: "Disabled",
    fovCorrection: 0,
    nearClipPlane: 10,
    backgroundType: "Scene",
    renderDimensions: false,
  },
  selectedVariants: {
    Background: {},
    Floor: {},
    Wall: {},
  },
  objects: [],
  currentCategoryFolder: null,
  selectObjects: {},
  objectProcessData: {
    imageHash: null,
    uiProductLoadId: null,
    renderGLBHash: null,
    tempSelectData: null,
    prevProductUiId: null,
  },
  productsCache: {},
  activeFolder: null,
  templateFolders: [],
  templates: [],
  isTemplatesLoading: false,
  templatesError: null,
  templatesCount: 0,
  foldersCount: 0,
  currentTemplate: null,
  currentTemplatePresets: [],
  // environments = stages
  environments: [],
  environmentsStage: [],
  environmentActiveValues: [],
  currentEnvironment: undefined,
  dataHistory: null,
  screenSettings: {
    aspectRatio: defaultAspect,
    quality: "Full HD",
    wrapperDimensions: { w: 1920, h: 1080 },
  },
  organizationId: REACT_APP_NODE_ENV_KEY === "production" ? 16 : 16,
  lod: 1, // Level of Detail for GLB rendering
  searchData: {
    searchValue: "",
    searchFolders: [],
    searchProjects: [],
    searchProducts: [],
  },
};

const uiSlice = createSlice({
  name: "ui",
  initialState,
  reducers: {
    setEnvironments(state, action) {
      const { environments } = action.payload;
      state.environments = environments;
    },
    setEnvironmentActiveValue(state, action) {
      state.environmentActiveValues = action.payload;
    },
    setEnvironmentVariant(state, action) {
      const { envName, envValue } = action.payload;
      state.selectedVariants = {
        ...state.selectedVariants,
        [envName]: envValue,
      };
    },
    setActiveTab(state, action) {
      const { activeTab } = action.payload;
      state.activeTab = activeTab;
    },
    setRenderHash(state, action) {
      const { hash } = action.payload;
      state.renderHash = hash;
    },
    setSelectedModel(state, action) {
      const {
        selectedModel = state.selectedModel,
        selectedType = state.selectedType,
      } = action.payload;
      state.selectedModel = selectedModel;
      state.selectedType = selectedType;
    },
    setActiveEnvironmentId(state, action) {
      state.activeEnvironmentId = action.payload;
    },
    setActiveObjectId(state, action) {
      state.activeObjectId = action.payload;
    },

    setDataHistory(state, action: PayloadAction<{ dataHistory: any }>) {
      const { dataHistory } = action.payload;
      state.dataHistory = dataHistory;
    },
    setDataUI(state, action) {
      const payload = action.payload;
      state.activeEnvironmentId = payload.activeEnvironmentIndex;
      state.environmentActiveValues = payload.environmentActiveValues;
      state.activeObjectId = payload.activeObjectIndex;
      state.selectedModel = payload.selectedModel;
      state.selectedType = payload.selectedType;
      state.activeTab = payload.activeTab;
      state.selectedVariants = payload.selectedVariants;

      state.selectObjects = payload.selectObjects;
    },

    setAspectRatio(state, action: PayloadAction<resolutionListT>) {
      state.screenSettings.aspectRatio = action.payload;
    },

    setQuality(state, action: PayloadAction<QualityType>) {
      state.screenSettings.quality = action.payload;
    },

    setWrapperDimensions(
      state,
      action: PayloadAction<{ w: number; h: number }>
    ) {
      state.screenSettings.wrapperDimensions = action.payload;
    },
    setActiveFolder(state, action: PayloadAction<IObject | null>) {
      if (action.payload) {
        const incoming = action.payload as IObject;

        // Ensure parent is set for deeper navigation (e.g., when children come from API without parent)
        let parent: IObject | null | undefined = incoming.parent;
        if (!parent && state.activeFolder) {
          const isChildOfActive = (state.activeFolder.children || []).some(
            c => c.id === incoming.id
          );
          const isChildOfCurrentCategory = (
            state.currentCategoryFolder?.children || []
          ).some(c => c.id === incoming.id);

          if (isChildOfActive || isChildOfCurrentCategory) {
            parent = state.activeFolder;
          }
        }

        const next: IObject = {
          ...incoming,
          parent: parent ?? incoming.parent,
        };

        state.activeFolder = {
          ...next,
          children: (incoming.children || []).map(child => ({
            ...child,
            parent: next,
          })),
        };
      } else {
        state.activeFolder = null;
        state.currentCategoryFolder = null;
      }
    },
    setCameraConfig(state, action: PayloadAction<typeof state.camera>) {
      state.camera = action.payload;
    },
    updateCameraSetting(
      state,
      action: PayloadAction<{
        key: "fov" | "focalDistance" | "sensorWidth" | "exposure";
        value: number | null;
      }>
    ) {
      const { key, value } = action.payload;
      (state.camera as any)[key] = value;
    },
    updateCameraSettingEnabled(
      state,
      action: PayloadAction<{
        key: "focalDistance" | "sensorWidth" | "exposure";
        enabled: boolean;
      }>
    ) {
      const { key, enabled } = action.payload;
      state.camera.enabledSettings[key] = enabled;
    },
    setCameraTransform(
      state,
      action: PayloadAction<{
        position: { x: number; y: number; z: number };
        rotation: { yaw: number; roll: number; pitch: number };
        scale: { x: number; y: number; z: number };
      }>
    ) {
      state.camera.transform = action.payload;
    },
    setSelectObjectData(
      state,
      action: PayloadAction<{ id: string; data: ISelectItemObjectData }>
    ) {
      const { id, data } = action.payload;
      state.selectObjects[id] = data;
    },
    replaceSelectObjectData(
      state,
      action: PayloadAction<{
        prevId: string;
        id: string;
        data: ISelectItemObjectData;
      }>
    ) {
      const { prevId, id, data } = action.payload;

      if (state.selectObjects[prevId]) {
        delete state.selectObjects[prevId];
      }

      state.selectObjects[id] = data;

      if (state.objectProcessData.uiProductLoadId === prevId) {
        state.objectProcessData = {
          uiProductLoadId: null,
          tempSelectData: null,
          imageHash: null,
          renderGLBHash: null,
          prevProductUiId: null,
        };
      }
    },
    removeSelectObjectData(state, action: PayloadAction<{ id: string }>) {
      const { id } = action.payload;
      if (state.selectObjects[id]) {
        delete state.selectObjects[id];
      }

      if (state.objectProcessData.uiProductLoadId === id) {
        state.objectProcessData.imageHash = null;
        state.objectProcessData.renderGLBHash = null;
        state.objectProcessData.uiProductLoadId = null;
        state.objectProcessData.tempSelectData = null;
      }

      if (state.activeObjectId === id) {
        state.activeObjectId = null;
      }
    },
    updateProcessObjectImage(
      state,
      action: PayloadAction<{
        id: string | null;
        prevProductId: string | null;
        imageHash: string | null;
        selectData: ISelectItemObjectData | null;
      }>
    ) {
      const { imageHash, id, selectData, prevProductId } = action.payload;
      state.objectProcessData = {
        ...state.objectProcessData,
        tempSelectData: selectData,
        imageHash: imageHash,
        uiProductLoadId: id,
        prevProductUiId: prevProductId,
      };
    },
    updateProcessGLBData(
      state,
      action: PayloadAction<{
        id: string | null;
        prevProductId: string | null;
        glbHash: string | null;
        selectData: ISelectItemObjectData | null;
      }>
    ) {
      const { id, glbHash, selectData, prevProductId } = action.payload;
      state.objectProcessData = {
        ...state.objectProcessData,
        renderGLBHash: glbHash,
        uiProductLoadId: id,
        tempSelectData: selectData,
        prevProductUiId: prevProductId,
      };
    },
    resetDataScene(state) {
      state.renderHash = "";
      state.selectObjects = {};
      state.objectProcessData = {
        imageHash: null,
        uiProductLoadId: null,
        renderGLBHash: null,
        tempSelectData: null,
        prevProductUiId: null,
      };
      state.activeFolder = null;

      state.currentCategoryFolder = null;
      state.activeObjectId = null;

      state.dataHistory = null;
      state.currentTemplate = null;
      state.currentTemplatePresets = [];
    },
    setCurrentEnvironment(state, action) {
      state.currentEnvironment = action.payload;
    },
    updateSearchData(state, action: PayloadAction<Partial<ISearchData>>) {
      state.searchData = {
        ...state.searchData,
        ...action.payload,
      };
    },
  },
  extraReducers: builder =>
    builder
      .addCase(
        getProjectStagesThunk.fulfilled,
        (state: initialUiStateT, action: PayloadAction<IStudioStage>) => {
          const stage = action.payload;

          state.environmentsStage = stage.enabled ? [stage] : [];

          if (stage.enabled) {
            state.activeEnvironmentId = stage.id;
          }
        }
      )
      .addCase(
        searchProductsThunk.fulfilled,
        (
          state: initialUiStateT,
          action: PayloadAction<ISearchProjectsResponseData>
        ) => {
          const rows = action.payload?.rows || [];

          const mapped = rows.map((row: any) => ({
            project: {
              id: row.id,
              name: row.name,
              imgCover: row.image,
              image: row.image,
              availableProducts: [],
            },
            pathToProject: [],
          }));

          state.searchData = {
            ...state.searchData,
            searchProjects: mapped,
            searchProducts: [],
          };
        }
      )
      .addCase(
        getProjectThunk.fulfilled,
        (
          state: initialUiStateT,
          action: PayloadAction<IProjectConfigurationResponseData>
        ) => {
          state.environments = action.payload.rows.filter(
            stage => stage.enabled
          );
          const queryString = window.location.search;
          const params = new URLSearchParams(queryString);
          const listId = params.get("list_id");
          if (state.environments.length === 0) {
            state.activeEnvironmentId = 0;
          } else if (Number(listId)) {
            state.activeEnvironmentId = Number(listId);
          } else {
            state.activeEnvironmentId = state.environments[0]?.id ?? 0;
          }
        }
      )
      .addCase(
        getCategoriesThunk.fulfilled,
        (state: initialUiStateT, action: PayloadAction<any>) => {
          state.objects = ObjectService.formattedObjectData(action.payload);
        }
      )
      .addCase(
        getCategoryInfoThunk.fulfilled,
        (state: initialUiStateT, action: PayloadAction<IStudioCategory>) => {
          state.currentCategoryFolder = action.payload;
        }
      )
      .addCase(
        getProductsThunk.fulfilled,
        (
          state: initialUiStateT,
          action: PayloadAction<IProductsResponseData>
        ) => {
          if (action.payload.rows.length === 0) return;

          const projectId = action.payload.rows[0].projectId;

          // Cache products by project for reliable reselection lookups
          state.productsCache[projectId] = action.payload.rows;

          // Update the currentCategoryFolder's projects with the fetched products
          if (state.currentCategoryFolder) {
            const project = state.currentCategoryFolder.projects.find(
              p => p.id === projectId
            );
            if (project) {
              project.availableProducts = action.payload.rows;
            }
          }

          // Also update in state.objects (the main category tree)
          const updateProjectInObjects = (objects: IObject[]): boolean => {
            for (const obj of objects) {
              const project = obj.projects?.find(p => p.id === projectId);
              if (project) {
                project.availableProducts = action.payload.rows;
                return true;
              }
              if (obj.children && obj.children.length > 0) {
                if (updateProjectInObjects(obj.children)) {
                  return true;
                }
              }
            }
            return false;
          };

          updateProjectInObjects(state.objects);
        }
      )
      .addCase(
        getCurrentStageThunk.fulfilled,
        (state: initialUiStateT, action: PayloadAction<any>) => {
          state.currentEnvironment = action.payload;
        }
      )
      .addCase(
        fetchTemplateFoldersThunk.fulfilled,
        (
          state: initialUiStateT,
          action: PayloadAction<ITemplateFoldersResponseData>
        ) => {
          const folders = action.payload?.rows || [];
          state.templateFolders = ObjectService.formattedObjectData(folders);
          state.foldersCount = action.payload?.count || 0;
        }
      )
      .addCase(
        createTemplateFolderThunk.fulfilled,
        (
          state: initialUiStateT,
          action: PayloadAction<ICreateTemplateFolderResponseData>
        ) => {
          // The folder list will be updated by the re-fetch in the thunk
          // No additional state changes needed here
        }
      )
      .addCase(fetchTemplatesThunk.pending, (state: initialUiStateT) => {
        state.isTemplatesLoading = true;
        state.templatesError = null;
      })
      .addCase(
        fetchTemplatesThunk.fulfilled,
        (
          state: initialUiStateT,
          action: PayloadAction<ITemplatesResponseData>
        ) => {
          state.isTemplatesLoading = false;
          state.templates = action.payload?.rows || [];
          state.templatesCount = action.payload?.count || 0;
        }
      )
      .addCase(
        fetchTemplatesThunk.rejected,
        (state: initialUiStateT, action) => {
          state.isTemplatesLoading = false;
          state.templatesError =
            action.error.message || "Failed to load templates";
        }
      )
      .addCase(
        restoreTemplateThunk.fulfilled,
        (state: initialUiStateT, action: PayloadAction<any>) => {
          const template = action.payload;
          state.currentTemplate = template;

          // Extract camera presets from the template response
          if (template.cameraPresets && Array.isArray(template.cameraPresets)) {
            state.currentTemplatePresets = template.cameraPresets.map(
              (preset: any) => ({
                id: String(preset.id),
                label: preset.name,
                config: preset.camera,
              })
            );
          } else {
            state.currentTemplatePresets = [];
          }
        }
      ),
});

export const {
  setEnvironments,
  setEnvironmentActiveValue,
  setEnvironmentVariant,
  setActiveTab,
  setRenderHash,
  setSelectedModel,
  setActiveEnvironmentId,
  setDataHistory,
  setDataUI,
  setAspectRatio,
  setQuality,
  setWrapperDimensions,
  setActiveFolder,
  setCameraConfig,
  updateProcessGLBData,
  updateCameraSetting,
  updateCameraSettingEnabled,
  setCameraTransform,
  setSelectObjectData,
  updateProcessObjectImage,
  resetDataScene,
  setActiveObjectId,
  removeSelectObjectData,
  replaceSelectObjectData,
  setCurrentEnvironment,
  updateSearchData,
} = uiSlice.actions;
export const uiReducer = uiSlice.reducer;
